/**
 * @file useWiseTableState.ts
 *
 * Facade hook dla WiseTable: orkiestruje wszystkie hooki tabeli i zwraca jeden obiekt stanu
 * gotowy do wstrzyknięcia przez WiseTableContext. Używany wyłącznie wewnątrz WiseTableProvider.
 *
 * Architektura:
 *   props (IWiseTableProps)
 *      → useWiseTableState (ten hook)
 *           → useWiseTableData, useWiseTableParameters, useWiseTableActions,
 *             useWiseTableAdvancedFilters, useWiseTableColumns, useWiseTableMaterialReactTable
 *      → IWiseTableContextValue
 *           → WiseTableContext.Provider
 *                → useWiseTable() w komponentach potomnych
 *
 * Nie wywoływać bezpośrednio w komponentach – używać useWiseTable() wewnątrz WiseTable.
 */

import { useCallback, useEffect, useMemo } from 'react';
import type { IWiseTableProps } from 'components/wise/types';
import type { IWiseTableContextValue } from 'components/wise/WiseTable/context/types';
import { useWiseTableData } from 'components/wise/WiseTable/hooks/useWiseTableData';
import { useWiseTableParameters } from 'components/wise/WiseTable/hooks/useWiseTableParameters';
import { useWiseTableActions } from 'components/wise/WiseTable/hooks/useWiseTableActions';
import { useWiseTableAdvancedFilters } from 'components/wise/WiseTable/hooks/useWiseTableAdvancedFilters';
import { useWiseTableColumns } from 'components/wise/WiseTable/hooks/useWiseTableColumns';
import { useWiseTableMaterialReactTable } from 'components/wise/WiseTable/hooks/useWiseTableMaterialReactTable';

/**
 * Łączy wszystkie hooki WiseTable w jeden stan kontekstu.
 *
 * Przyjmuje pełną konfigurację tabeli (IWiseTableProps), wywołuje wewnętrznie hooki
 * odpowiedzialne za dane, parametry, akcje, zaawansowane filtry, kolumny i Material React Table,
 * a następnie zwraca jeden obiekt zgodny z IWiseTableContextValue. Wartość ta jest przekazywana
 * do WiseTableProvider i odczytywana przez komponenty potomne przez useWiseTable().
 *
 * @param props - Konfiguracja tabeli (label, columns, loadDataParams, parameters, actions itd.)
 * @returns Stan tabeli: config, dane, paginacja, parametry, akcje, advancedFilters, instancja MRT, pochodne (tytuł, recordCount)
 *
 * @example
 * // Użycie wewnątrz WiseTableProvider (nie wywoływać w komponentach użytkownika):
 * function WiseTableProvider({ props, children }) {
 *   const value = useWiseTableState(props);
 *   return <WiseTableContext.Provider value={value}>{children}</WiseTableContext.Provider>;
 * }
 */
export function useWiseTableState(props: IWiseTableProps): IWiseTableContextValue {
  const {
    label,
    pageSymbol,
    componentSymbol,
    parameters = [],
    filters = [],
    columns,
    actions = [],
    loadDataParams,
    requestParams,
    componentParams,
    defaultLimit = 20,
    limitOptions = [10, 20, 50, 100],
    rowKey = 'id',
    showRecordCountInTitle = true,
    isCondensed = false,
    showPlainTable = false,
    withAdvancedFilters = false,
    onDataLoad,
    labelIcon
  } = props;

  // —— Dane: fetch, paginacja, sortowanie, synchronizacja z URL/Redux
  const {
    tableData,
    queryParams,
    setQueryParams,
    refetchTableData,
    isLoading,
    handlePageChange,
    handleLimitChange,
    handleSortChange
  } = useWiseTableData({
    loadDataParams,
    requestParams,
    pageSymbol,
    componentSymbol,
    parameters,
    filters,
    defaultLimit
  });

  // —— Parametry nad tabelą (toolbar): konfiguracja, aktywność, update, clear
  const {
    parameters: parametersConfig,
    activeParameters,
    handleClearParameters,
    updateParameter
  } = useWiseTableParameters({
    parameters,
    queryParams,
    setQueryParams,
    pageSymbol,
    componentSymbol
  });

  // —— Akcje: NO_CONTEXT, CONTEXT_MAIN/ADDITIONAL, HEADER (open), Reorder (drag & drop)
  const {
    noContextActions,
    mainActions,
    additionalActions,
    openActions,
    columnActions,
    handleReorder,
    isReordering,
    hasReorderAction
  } = useWiseTableActions({
    actions,
    refetchTableData
  });

  // —— Zaawansowane filtry: reguły AND/OR, widoczność i kolejność kolumn, presety, ColumnFilterPopper
  const advancedFilters = useWiseTableAdvancedFilters({
    columns,
    parameters,
    filters,
    pageSymbol,
    componentSymbol
  });

  const {
    columnVisibility,
    columnOrder,
    onColumnVisibilityChange,
    onOpenColumnFilterDialog,
    showColumnFilterIcons,
    getColumnFilterRowsForColumn,
    selectedSettingsPresetName,
    savedSettingsOptionsForSelect
  } = advancedFilters;

  // —— Kolumny: mapowanie IWiseTableColumn → MRT_ColumnDef + kolumna akcji
  const mrtColumns = useWiseTableColumns({
    columns,
    mainActions,
    additionalActions,
    columnActions,
    refetchTableData,
    requestParams
  });

  const isTableLoading = isLoading || isReordering;

  // —— Dane do renderu: przy ładowaniu – szkielety; potem items z API
  const tableDataForRender = useMemo(() => {
    if (isTableLoading && (!tableData?.items || tableData.items.length === 0)) {
      const skeletonRowsCount = queryParams.limit || defaultLimit;
      return Array.from({ length: skeletonRowsCount }, (_, index) => ({
        [rowKey]: `skeleton-${index}`
      }));
    }
    return tableData?.items || [];
  }, [isTableLoading, tableData?.items, queryParams.limit, defaultLimit, rowKey]);

  // —— Kolejność kolumn: z Redux (po „Zastosuj”) lub domyślna z mrtColumns; kolumna „actions” na końcu
  const tableColumnOrder = useMemo(() => {
    if (columnOrder.length > 0) {
      return mainActions.length > 0 || additionalActions.length > 0
        ? [...columnOrder, 'actions']
        : columnOrder;
    }
    return mrtColumns.map((col) => String(col.id));
  }, [columnOrder, mainActions.length, additionalActions.length, mrtColumns]);

  // —— Helper: czy dla danej kolumny jest ustawiony filtr (ikona FilterCircle w nagłówku)
  const getColumnHasFilter = useCallback(
    (columnId: string) => {
      const rows = getColumnFilterRowsForColumn?.(columnId) ?? [];
      return rows.some((r) => r.operator != null && String(r.value ?? '').trim() !== '');
    },
    [getColumnFilterRowsForColumn]
  );

  // —— Material React Table: konfiguracja MRT (sortowanie, reorder, filtry w nagłówku, style)
  const materialReactTable = useWiseTableMaterialReactTable({
    columns: mrtColumns,
    data: tableDataForRender,
    rowKey,
    queryParams,
    tableData,
    handleSortChange,
    handleReorder,
    columnVisibility,
    onColumnVisibilityChange,
    columnOrder: tableColumnOrder,
    hasReorderAction,
    isLoading: isTableLoading,
    withAdvancedFilters,
    showColumnFilterIcons,
    onOpenColumnFilterDialog,
    getColumnHasFilter
  });

  useEffect(() => {
    if (onDataLoad && tableData) {
      onDataLoad(tableData, componentSymbol);
    }
  }, [onDataLoad, componentSymbol, tableData]);

  // —— Pochodne: tytuł z liczbą rekordów i nazwą presetu, config (stabilna referencja)
  const setting = useMemo(() => {
    if (!withAdvancedFilters || !selectedSettingsPresetName) return undefined;
    const option = savedSettingsOptionsForSelect?.find(
      (o) => o.value === selectedSettingsPresetName
    );
    return option?.label ?? undefined;
  }, [withAdvancedFilters, selectedSettingsPresetName, savedSettingsOptionsForSelect]);

  const recordCount = showRecordCountInTitle ? tableData?.total_count : undefined;
  const recordCountPart = recordCount ? ` (${recordCount})` : '';
  const titleWithSetting = setting
    ? `${label}${recordCountPart} (${setting})`
    : `${label}${recordCountPart}`;

  const config = useMemo(
    () => ({
      label,
      pageSymbol,
      componentSymbol,
      parameters,
      columns,
      filters,
      rowKey,
      defaultLimit,
      limitOptions,
      isCondensed,
      showPlainTable,
      withAdvancedFilters,
      showRecordCountInTitle,
      componentParams,
      requestParams,
      labelIcon
    }),
    [
      label,
      pageSymbol,
      componentSymbol,
      parameters,
      columns,
      filters,
      rowKey,
      defaultLimit,
      limitOptions,
      isCondensed,
      showPlainTable,
      withAdvancedFilters,
      showRecordCountInTitle,
      componentParams,
      requestParams,
      labelIcon
    ]
  );

  return {
    config,
    tableData,
    queryParams,
    refetchTableData,
    isTableLoading,
    handlePageChange,
    handleLimitChange,
    parametersConfig,
    activeParameters,
    handleClearParameters,
    updateParameter,
    noContextActions,
    mainActions,
    additionalActions,
    openActions,
    handleReorder,
    hasReorderAction,
    advancedFilters,
    materialReactTable,
    titleWithSetting,
    recordCount
  };
}
